import React, { useState } from 'react';
import { Container, Card, CardBody, Nav, NavItem, NavLink, TabContent, TabPane, Row, Col, FormGroup, Label, Input, Button, FormFeedback, Spinner } from 'reactstrap';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import { market } from '../static/brands_wholesalers_data';
import { inviteOrganization as inviteOrganizationAction } from '../ReduxToolkit/Reducers/userManagement';
import { getReferralCodes } from '../ReduxToolkit/Reducers/organizationManagement';
import InviteSuccessModal from '../Components/InviteSuccessModal';

// Validation schemas
const brandValidationSchema = yup.object().shape({
  brandWebsite: yup
    .string()
    .trim()
    .matches(/^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/.*)?$/i, 'Enter a valid website URL')
    .required('Brand website is required'),
  organizationName: yup.string().trim().required('Organization name is required'),
  personalName: yup.string().trim().required('Personal name is required'),
  role: yup.string().trim().optional().nullable(),
  businessEmail: yup
    .string()
    .trim()
    .email('Enter a valid email')
    .required('Business email is required'),
  phone: yup
    .string()
    .trim()
    .nullable()
    .notRequired()
    .test({
      test: (value) => {
        if (!value) return true;
        return /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/i.test(value);
      },
      message: 'Enter a valid phone number'
    }),
  marketsOfInterest: yup.array().optional().nullable(),
  trialPeriod: yup.string().optional().nullable()
});

const retailerValidationSchema = yup.object().shape({
  brandWebsite: yup
    .string()
    .trim()
    .matches(/^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/.*)?$/i, 'Enter a valid website URL')
    .required('Retailer website is required'),
  brandName: yup.string().trim().required('Retailer name is required'),
  personalName: yup.string().trim().required('Personal name is required'),
  businessEmail: yup
    .string()
    .trim()
    .email('Enter a valid email')
    .required('Business email is required'),
  phone: yup
    .string()
    .trim()
    .nullable()
    .notRequired()
    .test({
      test: (value) => {
        if (!value) return true;
        return /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/i.test(value);
      },
      message: 'Enter a valid phone number'
    }),
  role: yup.string().trim().optional().nullable(),
  trialPeriod: yup.string().optional().nullable()
});

const salesAgentValidationSchema = yup.object().shape({
  brandName: yup.string().trim().nullable().notRequired(),
  brandWebsite: yup
    .string()
    .trim()
    .nullable()
    .notRequired()
    .test({
      test: (value) => {
        if (!value) return true;
        return /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/.*)?$/i.test(value);
      },
      message: 'Enter a valid website URL'
    }),
  organizationName: yup.string().trim().required('Organization name is required'),
  organizationWebsite: yup
    .string()
    .trim()
    .nullable()
    .notRequired()
    .test({
      test: (value) => {
        if (!value) return true;
        return /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/.*)?$/i.test(value);
      },
      message: 'Enter a valid website URL'
    }),
  personalName: yup.string().trim().required('Personal name is required'),
  phone: yup
    .string()
    .trim()
    .nullable()
    .notRequired()
    .test({
      test: (value) => {
        if (!value) return true;
        return /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/i.test(value);
      },
      message: 'Enter a valid phone number'
    }),
  businessEmail: yup
    .string()
    .trim()
    .email('Enter a valid email')
    .required('Business email is required'),
  role: yup.string().trim().optional().nullable(),
  trialPeriod: yup.string().optional().nullable()
});

function InvitePage({ inviteOrganization }) {
  const [activeTab, setActiveTab] = useState('brand');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [referralOptions, setReferralOptions] = useState([]);
  const [selectedBrandReferral, setSelectedBrandReferral] = useState(null);
  const [selectedRetailerReferral, setSelectedRetailerReferral] = useState(null);
  const [selectedAgentReferral, setSelectedAgentReferral] = useState(null);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [invitedOrganizationName, setInvitedOrganizationName] = useState('');

  // Load all referral codes on mount
  React.useEffect(() => {
    const loadReferralCodes = async () => {
      const result = await dispatch(getReferralCodes(1, '', 200));
      if (result?.data?.data) {
        setReferralOptions(
          result.data.data.map((r) => ({
            value: r.referral_code,
            label: `${r.referral_name} (${r.referral_code})`,
          }))
        );
      }
    };
    loadReferralCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter out "Open" option from markets
  const marketOptions = market.filter((m) => m.code !== 'OP');

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  // Brand Form
  const {
    control: brandControl,
    handleSubmit: handleBrandSubmit,
    formState: { errors: brandErrors, isSubmitting: brandSubmitting },
    reset: brandReset
  } = useForm({
    resolver: yupResolver(brandValidationSchema),
    defaultValues: {
      brandWebsite: '',
      organizationName: '',
      businessEmail: '',
      personalName: '',
      role: '',
      phone: '',
      marketsOfInterest: [],
      trialPeriod: '14'
    }
  });

  // Retailer Form
  const {
    control: retailerControl,
    handleSubmit: handleRetailerSubmit,
    formState: { errors: retailerErrors, isSubmitting: retailerSubmitting },
    reset: retailerReset
  } = useForm({
    resolver: yupResolver(retailerValidationSchema),
    defaultValues: {
      brandWebsite: '',
      brandName: '',
      personalName: '',
      businessEmail: '',
      phone: '',
      role: '',
      trialPeriod: '14'
    }
  });

  // Sales Agent Form
  const {
    control: salesAgentControl,
    handleSubmit: handleSalesAgentSubmit,
    formState: { errors: salesAgentErrors, isSubmitting: salesAgentSubmitting },
    reset: salesAgentReset
  } = useForm({
    resolver: yupResolver(salesAgentValidationSchema),
    defaultValues: {
      brandName: '',
      brandWebsite: '',
      organizationName: '',
      organizationWebsite: '',
      phone: '',
      businessEmail: '',
      personalName: '',
      role: '',
      trialPeriod: '14'
    }
  });

  const handleInviteSuccess = (organizationName, resetForm, clearReferral) => {
    resetForm();
    clearReferral();
    setInvitedOrganizationName(organizationName);
    setSuccessModalOpen(true);
  };

  const handleCloseSuccessModal = () => {
    setSuccessModalOpen(false);
    setInvitedOrganizationName('');
  };

  // Submit handlers
  const onSubmitBrand = async (data) => {
    try {
      const payload = {
        website: data.brandWebsite,
        name: data.organizationName,
        role: data.role || 'user',
        email: data.businessEmail,
        phone: data.phone || '',
        username: data.personalName || data.organizationName,
        type: 'brand',
        org_source: 'admin',
        markets_of_interest: data.marketsOfInterest?.length
          ? data.marketsOfInterest.map((m) => (typeof m === 'string' ? m : m.label))
          : ['Open']
      };

      if (selectedBrandReferral) {
        payload.referral = selectedBrandReferral.value;
      } else {
        payload.trial_period = data.trialPeriod ? parseInt(data.trialPeriod) : 14;
      }

      const result = await inviteOrganization(payload);

      if (result?.statusCode === 200) {
        handleInviteSuccess(data.organizationName, brandReset, () => setSelectedBrandReferral(null));
      }
    } catch (error) {
      console.error('Error inviting brand:', error);
    }
  };

  const onSubmitRetailer = async (data) => {
    try {
      const payload = {
        website: data.brandWebsite,
        name: data.brandName,
        role: data.role || 'user',
        email: data.businessEmail,
        phone: data.phone || '',
        username: data.personalName || data.brandName,
        org_source: 'admin',
        type: 'retailer',
      };

      if (selectedRetailerReferral) {
        payload.referral = selectedRetailerReferral.value;
      } else {
        payload.trial_period = data.trialPeriod ? parseInt(data.trialPeriod) : 14;
      }

      const result = await inviteOrganization(payload);

      if (result?.statusCode === 200) {
        handleInviteSuccess(data.brandName, retailerReset, () => setSelectedRetailerReferral(null));
      }
    } catch (error) {
      console.error('Error inviting retailer:', error);
    }
  };

  const onSubmitSalesAgent = async (data) => {
    try {
      const payload = {
        brand_name: data.brandName || '',
        brand_website: data.brandWebsite || '',
        org_name: data.organizationName,
        email: data.businessEmail,
        org_website: data.organizationWebsite || '',
        role: data.role || 'user',
        phone: data.phone || '',
        username: data.personalName,
        org_source: 'admin',
        type: 'sales_agent',
      };

      if (selectedAgentReferral) {
        payload.referral = selectedAgentReferral.value;
      } else {
        payload.trial_period = data.trialPeriod ? parseInt(data.trialPeriod) : 14;
      }

      const result = await inviteOrganization(payload);

      if (result?.statusCode === 200) {
        handleInviteSuccess(data.organizationName, salesAgentReset, () => setSelectedAgentReferral(null));
      }
    } catch (error) {
      console.error('Error inviting sales agent:', error);
    }
  };

  return (
    <div className="page-body">
      <Container fluid>
        <Row className="justify-content-center">
          <Col lg={10} xl={8}>
            <Card className="shadow-sm">
              <CardBody className="p-4">
                <div className="mb-4">
                  <h4 className="card-title mb-1" style={{ fontWeight: '600', fontSize: '20px' }}>
                    Invite Organization
                  </h4>
                  <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                    Send invitations to brands, retailers, or sales agents
                  </p>
                </div>

                <Nav tabs className="nav-tabs-custom mb-4">
                  <NavItem>
                    <NavLink
                      className={activeTab === 'brand' ? 'active' : ''}
                      onClick={() => toggleTab('brand')}
                      style={{
                        cursor: 'pointer',
                        fontWeight: activeTab === 'brand' ? '600' : '500',
                        fontSize: '15px'
                      }}
                    >
                      Invite Brand
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === 'retailer' ? 'active' : ''}
                      onClick={() => toggleTab('retailer')}
                      style={{
                        cursor: 'pointer',
                        fontWeight: activeTab === 'retailer' ? '600' : '500',
                        fontSize: '15px'
                      }}
                    >
                      Invite Retailer
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === 'sales_agent' ? 'active' : ''}
                      onClick={() => toggleTab('sales_agent')}
                      style={{
                        cursor: 'pointer',
                        fontWeight: activeTab === 'sales_agent' ? '600' : '500',
                        fontSize: '15px'
                      }}
                    >
                      Invite Sales Agent
                    </NavLink>
                  </NavItem>
                </Nav>

                <TabContent activeTab={activeTab}>
                  {/* Brand Tab */}
                  <TabPane tabId="brand">
                    <form onSubmit={handleBrandSubmit(onSubmitBrand)}>
                      <Row className="g-3">
                        <Col md={6}>
                          <FormGroup className="mb-0">
                            <Label className="form-label" style={{ fontWeight: '500', fontSize: '14px' }}>
                              Brand Website <span className="text-danger">*</span>
                            </Label>
                            <Controller
                              name="brandWebsite"
                              control={brandControl}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="text"
                                  placeholder="e.g www.LouisVuitton.com"
                                  invalid={!!brandErrors.brandWebsite}
                                  style={{ fontSize: '14px' }}
                                />
                              )}
                            />
                            {brandErrors.brandWebsite && (
                              <FormFeedback>{brandErrors.brandWebsite.message}</FormFeedback>
                            )}
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="mb-0">
                            <Label className="form-label" style={{ fontWeight: '500', fontSize: '14px' }}>
                              Brand Name <span className="text-danger">*</span>
                            </Label>
                            <Controller
                              name="organizationName"
                              control={brandControl}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="text"
                                  placeholder="e.g Louis Vuitton"
                                  invalid={!!brandErrors.organizationName}
                                  style={{ fontSize: '14px' }}
                                />
                              )}
                            />
                            {brandErrors.organizationName && (
                              <FormFeedback>{brandErrors.organizationName.message}</FormFeedback>
                            )}
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="mb-0">
                            <Label className="form-label" style={{ fontWeight: '500', fontSize: '14px' }}>
                              Personal Name <span className="text-danger">*</span>
                            </Label>
                            <Controller
                              name="personalName"
                              control={brandControl}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="text"
                                  placeholder="e.g John Doe"
                                  invalid={!!brandErrors.personalName}
                                  style={{ fontSize: '14px' }}
                                />
                              )}
                            />
                            {brandErrors.personalName && (
                              <FormFeedback>{brandErrors.personalName.message}</FormFeedback>
                            )}
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="mb-0">
                            <Label className="form-label" style={{ fontWeight: '500', fontSize: '14px' }}>
                              Role
                            </Label>
                            <Controller
                              name="role"
                              control={brandControl}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="text"
                                  placeholder="e.g Market Lead"
                                  invalid={!!brandErrors.role}
                                  style={{ fontSize: '14px' }}
                                />
                              )}
                            />
                            {brandErrors.role && (
                              <FormFeedback>{brandErrors.role.message}</FormFeedback>
                            )}
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="mb-0">
                            <Label className="form-label" style={{ fontWeight: '500', fontSize: '14px' }}>
                              Business Email <span className="text-danger">*</span>
                            </Label>
                            <Controller
                              name="businessEmail"
                              control={brandControl}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="email"
                                  placeholder="e.g Louis@gmail.com"
                                  invalid={!!brandErrors.businessEmail}
                                  style={{ fontSize: '14px' }}
                                />
                              )}
                            />
                            {brandErrors.businessEmail && (
                              <FormFeedback>{brandErrors.businessEmail.message}</FormFeedback>
                            )}
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="mb-0">
                            <Label className="form-label" style={{ fontWeight: '500', fontSize: '14px' }}>
                              Phone Number
                            </Label>
                            <Controller
                              name="phone"
                              control={brandControl}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="tel"
                                  placeholder="92 123 456 7890 (Optional)"
                                  invalid={!!brandErrors.phone}
                                  style={{ fontSize: '14px' }}
                                />
                              )}
                            />
                            {brandErrors.phone && (
                              <FormFeedback>{brandErrors.phone.message}</FormFeedback>
                            )}
                          </FormGroup>
                        </Col>
                        <Col md={12}>
                          <FormGroup className="mb-0">
                            <Label className="form-label" style={{ fontWeight: '500', fontSize: '14px' }}>
                              Markets of Interest
                            </Label>
                            <Controller
                              name="marketsOfInterest"
                              control={brandControl}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  isMulti
                                  options={marketOptions}
                                  getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                                  getOptionValue={(option) => typeof option === 'string' ? option : option.label}
                                  placeholder="Select countries"
                                  className={brandErrors.marketsOfInterest ? 'is-invalid' : ''}
                                  styles={{
                                    control: (base) => ({ ...base, fontSize: '14px' }),
                                    option: (base) => ({ ...base, fontSize: '14px' })
                                  }}
                                />
                              )}
                            />
                            {brandErrors.marketsOfInterest && (
                              <FormFeedback style={{ display: 'block' }}>
                                {brandErrors.marketsOfInterest.message}
                              </FormFeedback>
                            )}
                          </FormGroup>
                        </Col>
                        <Col md={12}>
                          <FormGroup className="mb-0">
                            <Label className="form-label" style={{ fontWeight: '500', fontSize: '14px' }}>
                              Referral Code <span className="text-muted" style={{ fontWeight: '400' }}>(Optional)</span>
                            </Label>
                            <Select
                              isClearable
                              isSearchable
                              options={referralOptions}
                              value={selectedBrandReferral}
                              onChange={setSelectedBrandReferral}
                              placeholder="Search referral code..."
                              styles={{
                                control: (base) => ({ ...base, fontSize: '14px' }),
                                option: (base) => ({ ...base, fontSize: '14px' })
                              }}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={12}>
                          <FormGroup className="mb-0">
                            <Label className="form-label" style={{ fontWeight: '500', fontSize: '14px', marginBottom: '10px' }}>
                              Trial Period {selectedBrandReferral && <span className="text-muted" style={{ fontWeight: '400', fontSize: '12px' }}>(Disabled — using referral code trial)</span>}
                            </Label>
                            <div style={{ opacity: selectedBrandReferral ? 0.4 : 1, pointerEvents: selectedBrandReferral ? 'none' : 'auto' }}>
                              <Controller
                                name="trialPeriod"
                                control={brandControl}
                                render={({ field }) => (
                                  <div className="d-flex gap-4">
                                    <div className="form-check">
                                      <Input
                                        {...field}
                                        type="radio"
                                        id="brand-trial-14"
                                        value="14"
                                        checked={field.value === '14'}
                                        className="form-check-input"
                                        style={{ cursor: 'pointer' }}
                                      />
                                      <Label
                                        className="form-check-label"
                                        htmlFor="brand-trial-14"
                                        style={{ cursor: 'pointer', fontSize: '14px', fontWeight: '400' }}
                                      >
                                        14 Days
                                      </Label>
                                    </div>
                                    <div className="form-check">
                                      <Input
                                        {...field}
                                        type="radio"
                                        id="brand-trial-30"
                                        value="30"
                                        checked={field.value === '30'}
                                        className="form-check-input"
                                        style={{ cursor: 'pointer' }}
                                      />
                                      <Label
                                        className="form-check-label"
                                        htmlFor="brand-trial-30"
                                        style={{ cursor: 'pointer', fontSize: '14px', fontWeight: '400' }}
                                      >
                                        30 Days
                                      </Label>
                                    </div>
                                  </div>
                                )}
                              />
                              {brandErrors.trialPeriod && (
                                <div className="text-danger" style={{ fontSize: '14px', marginTop: '4px' }}>
                                  {brandErrors.trialPeriod.message}
                                </div>
                              )}
                            </div>
                          </FormGroup>
                        </Col>
                      </Row>
                      <div className="mt-4 pt-2 border-top">
                        <Button
                          color="primary"
                          type="submit"
                          disabled={brandSubmitting}
                          size="lg"
                          style={{ minWidth: '200px', fontWeight: '500' }}
                        >
                          {brandSubmitting && <Spinner size="sm" className="me-2" />}
                          {brandSubmitting ? 'Sending Invite...' : 'Invite Brand'}
                        </Button>
                      </div>
                    </form>
                  </TabPane>

                  {/* Retailer Tab */}
                  <TabPane tabId="retailer">
                    <form onSubmit={handleRetailerSubmit(onSubmitRetailer)}>
                      <Row className="g-3">
                        <Col md={6}>
                          <FormGroup className="mb-0">
                            <Label className="form-label" style={{ fontWeight: '500', fontSize: '14px' }}>
                              Retailer Website <span className="text-danger">*</span>
                            </Label>
                            <Controller
                              name="brandWebsite"
                              control={retailerControl}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="text"
                                  placeholder="e.g www.retailstore.com"
                                  invalid={!!retailerErrors.brandWebsite}
                                  style={{ fontSize: '14px' }}
                                />
                              )}
                            />
                            {retailerErrors.brandWebsite && (
                              <FormFeedback>{retailerErrors.brandWebsite.message}</FormFeedback>
                            )}
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="mb-0">
                            <Label className="form-label" style={{ fontWeight: '500', fontSize: '14px' }}>
                              Retailer Name <span className="text-danger">*</span>
                            </Label>
                            <Controller
                              name="brandName"
                              control={retailerControl}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="text"
                                  placeholder="e.g ABC Retail Store"
                                  invalid={!!retailerErrors.brandName}
                                  style={{ fontSize: '14px' }}
                                />
                              )}
                            />
                            {retailerErrors.brandName && (
                              <FormFeedback>{retailerErrors.brandName.message}</FormFeedback>
                            )}
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="mb-0">
                            <Label className="form-label" style={{ fontWeight: '500', fontSize: '14px' }}>
                              Personal Name <span className="text-danger">*</span>
                            </Label>
                            <Controller
                              name="personalName"
                              control={retailerControl}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="text"
                                  placeholder="e.g John Doe"
                                  invalid={!!retailerErrors.personalName}
                                  style={{ fontSize: '14px' }}
                                />
                              )}
                            />
                            {retailerErrors.personalName && (
                              <FormFeedback>{retailerErrors.personalName.message}</FormFeedback>
                            )}
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="mb-0">
                            <Label className="form-label" style={{ fontWeight: '500', fontSize: '14px' }}>
                              Business Email <span className="text-danger">*</span>
                            </Label>
                            <Controller
                              name="businessEmail"
                              control={retailerControl}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="email"
                                  placeholder="e.g john@retailstore.com"
                                  invalid={!!retailerErrors.businessEmail}
                                  style={{ fontSize: '14px' }}
                                />
                              )}
                            />
                            {retailerErrors.businessEmail && (
                              <FormFeedback>{retailerErrors.businessEmail.message}</FormFeedback>
                            )}
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="mb-0">
                            <Label className="form-label" style={{ fontWeight: '500', fontSize: '14px' }}>
                              Phone Number
                            </Label>
                            <Controller
                              name="phone"
                              control={retailerControl}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="tel"
                                  placeholder="92 123 456 7890 (Optional)"
                                  invalid={!!retailerErrors.phone}
                                  style={{ fontSize: '14px' }}
                                />
                              )}
                            />
                            {retailerErrors.phone && (
                              <FormFeedback>{retailerErrors.phone.message}</FormFeedback>
                            )}
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="mb-0">
                            <Label className="form-label" style={{ fontWeight: '500', fontSize: '14px' }}>
                              Role
                            </Label>
                            <Controller
                              name="role"
                              control={retailerControl}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="text"
                                  placeholder="e.g Store Manager"
                                  invalid={!!retailerErrors.role}
                                  style={{ fontSize: '14px' }}
                                />
                              )}
                            />
                            {retailerErrors.role && (
                              <FormFeedback>{retailerErrors.role.message}</FormFeedback>
                            )}
                          </FormGroup>
                        </Col>
                        <Col md={12}>
                          <FormGroup className="mb-0">
                            <Label className="form-label" style={{ fontWeight: '500', fontSize: '14px' }}>
                              Referral Code <span className="text-muted" style={{ fontWeight: '400' }}>(Optional)</span>
                            </Label>
                            <Select
                              isClearable
                              isSearchable
                              options={referralOptions}
                              value={selectedRetailerReferral}
                              onChange={setSelectedRetailerReferral}
                              placeholder="Search referral code..."
                              styles={{
                                control: (base) => ({ ...base, fontSize: '14px' }),
                                option: (base) => ({ ...base, fontSize: '14px' })
                              }}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={12}>
                          <FormGroup className="mb-0">
                            <Label className="form-label" style={{ fontWeight: '500', fontSize: '14px', marginBottom: '10px' }}>
                              Trial Period {selectedRetailerReferral && <span className="text-muted" style={{ fontWeight: '400', fontSize: '12px' }}>(Disabled — using referral code trial)</span>}
                            </Label>
                            <div style={{ opacity: selectedRetailerReferral ? 0.4 : 1, pointerEvents: selectedRetailerReferral ? 'none' : 'auto' }}>
                              <Controller
                                name="trialPeriod"
                                control={retailerControl}
                                render={({ field }) => (
                                  <div className="d-flex gap-4">
                                    <div className="form-check">
                                      <Input
                                        {...field}
                                        type="radio"
                                        id="retailer-trial-14"
                                        value="14"
                                        checked={field.value === '14'}
                                        className="form-check-input"
                                        style={{ cursor: 'pointer' }}
                                      />
                                      <Label
                                        className="form-check-label"
                                        htmlFor="retailer-trial-14"
                                        style={{ cursor: 'pointer', fontSize: '14px', fontWeight: '400' }}
                                      >
                                        14 Days
                                      </Label>
                                    </div>
                                    <div className="form-check">
                                      <Input
                                        {...field}
                                        type="radio"
                                        id="retailer-trial-30"
                                        value="30"
                                        checked={field.value === '30'}
                                        className="form-check-input"
                                        style={{ cursor: 'pointer' }}
                                      />
                                      <Label
                                        className="form-check-label"
                                        htmlFor="retailer-trial-30"
                                        style={{ cursor: 'pointer', fontSize: '14px', fontWeight: '400' }}
                                      >
                                        30 Days
                                      </Label>
                                    </div>
                                  </div>
                                )}
                              />
                              {retailerErrors.trialPeriod && (
                                <div className="text-danger" style={{ fontSize: '14px', marginTop: '4px' }}>
                                  {retailerErrors.trialPeriod.message}
                                </div>
                              )}
                            </div>
                          </FormGroup>
                        </Col>
                      </Row>
                      <div className="mt-4 pt-2 border-top">
                        <Button
                          color="primary"
                          type="submit"
                          disabled={retailerSubmitting}
                          size="lg"
                          style={{ minWidth: '200px', fontWeight: '500' }}
                        >
                          {retailerSubmitting && <Spinner size="sm" className="me-2" />}
                          {retailerSubmitting ? 'Sending Invite...' : 'Invite Retailer'}
                        </Button>
                      </div>
                    </form>
                  </TabPane>

                  {/* Sales Agent Tab */}
                  <TabPane tabId="sales_agent">
                    <form onSubmit={handleSalesAgentSubmit(onSubmitSalesAgent)}>
                      <Row className="g-3">
                        <Col md={6}>
                          <FormGroup className="mb-0">
                            <Label className="form-label" style={{ fontWeight: '500', fontSize: '14px' }}>
                              Brand Name
                            </Label>
                            <Controller
                              name="brandName"
                              control={salesAgentControl}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="text"
                                  placeholder="e.g Louis Vuitton (Optional)"
                                  invalid={!!salesAgentErrors.brandName}
                                  style={{ fontSize: '14px' }}
                                />
                              )}
                            />
                            {salesAgentErrors.brandName && (
                              <FormFeedback>{salesAgentErrors.brandName.message}</FormFeedback>
                            )}
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="mb-0">
                            <Label className="form-label" style={{ fontWeight: '500', fontSize: '14px' }}>
                              Brand Website
                            </Label>
                            <Controller
                              name="brandWebsite"
                              control={salesAgentControl}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="text"
                                  placeholder="e.g www.LouisVuitton.com (Optional)"
                                  invalid={!!salesAgentErrors.brandWebsite}
                                  style={{ fontSize: '14px' }}
                                />
                              )}
                            />
                            {salesAgentErrors.brandWebsite && (
                              <FormFeedback>{salesAgentErrors.brandWebsite.message}</FormFeedback>
                            )}
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="mb-0">
                            <Label className="form-label" style={{ fontWeight: '500', fontSize: '14px' }}>
                              Organization Name <span className="text-danger">*</span>
                            </Label>
                            <Controller
                              name="organizationName"
                              control={salesAgentControl}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="text"
                                  placeholder="e.g Sales Company Inc."
                                  invalid={!!salesAgentErrors.organizationName}
                                  style={{ fontSize: '14px' }}
                                />
                              )}
                            />
                            {salesAgentErrors.organizationName && (
                              <FormFeedback>{salesAgentErrors.organizationName.message}</FormFeedback>
                            )}
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="mb-0">
                            <Label className="form-label" style={{ fontWeight: '500', fontSize: '14px' }}>
                              Organization Website
                            </Label>
                            <Controller
                              name="organizationWebsite"
                              control={salesAgentControl}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="text"
                                  placeholder="e.g www.salescompany.com (Optional)"
                                  invalid={!!salesAgentErrors.organizationWebsite}
                                  style={{ fontSize: '14px' }}
                                />
                              )}
                            />
                            {salesAgentErrors.organizationWebsite && (
                              <FormFeedback>{salesAgentErrors.organizationWebsite.message}</FormFeedback>
                            )}
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="mb-0">
                            <Label className="form-label" style={{ fontWeight: '500', fontSize: '14px' }}>
                              Personal Name <span className="text-danger">*</span>
                            </Label>
                            <Controller
                              name="personalName"
                              control={salesAgentControl}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="text"
                                  placeholder="e.g Jane Smith"
                                  invalid={!!salesAgentErrors.personalName}
                                  style={{ fontSize: '14px' }}
                                />
                              )}
                            />
                            {salesAgentErrors.personalName && (
                              <FormFeedback>{salesAgentErrors.personalName.message}</FormFeedback>
                            )}
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="mb-0">
                            <Label className="form-label" style={{ fontWeight: '500', fontSize: '14px' }}>
                              Business Email <span className="text-danger">*</span>
                            </Label>
                            <Controller
                              name="businessEmail"
                              control={salesAgentControl}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="email"
                                  placeholder="e.g john@retailstore.com"
                                  invalid={!!salesAgentErrors.businessEmail}
                                  style={{ fontSize: '14px' }}
                                />
                              )}
                            />
                            {salesAgentErrors.businessEmail && (
                              <FormFeedback>{salesAgentErrors.businessEmail.message}</FormFeedback>
                            )}
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="mb-0">
                            <Label className="form-label" style={{ fontWeight: '500', fontSize: '14px' }}>
                              Role
                            </Label>
                            <Controller
                              name="role"
                              control={salesAgentControl}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="text"
                                  placeholder="e.g Sales Representative"
                                  invalid={!!salesAgentErrors.role}
                                  style={{ fontSize: '14px' }}
                                />
                              )}
                            />
                            {salesAgentErrors.role && (
                              <FormFeedback>{salesAgentErrors.role.message}</FormFeedback>
                            )}
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="mb-0">
                            <Label className="form-label" style={{ fontWeight: '500', fontSize: '14px' }}>
                              Phone Number
                            </Label>
                            <Controller
                              name="phone"
                              control={salesAgentControl}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="tel"
                                  placeholder="92 123 456 7890 (Optional)"
                                  invalid={!!salesAgentErrors.phone}
                                  style={{ fontSize: '14px' }}
                                />
                              )}
                            />
                            {salesAgentErrors.phone && (
                              <FormFeedback>{salesAgentErrors.phone.message}</FormFeedback>
                            )}
                          </FormGroup>
                        </Col>
                        <Col md={12}>
                          <FormGroup className="mb-0">
                            <Label className="form-label" style={{ fontWeight: '500', fontSize: '14px' }}>
                              Referral Code <span className="text-muted" style={{ fontWeight: '400' }}>(Optional)</span>
                            </Label>
                            <Select
                              isClearable
                              isSearchable
                              options={referralOptions}
                              value={selectedAgentReferral}
                              onChange={setSelectedAgentReferral}
                              placeholder="Search referral code..."
                              styles={{
                                control: (base) => ({ ...base, fontSize: '14px' }),
                                option: (base) => ({ ...base, fontSize: '14px' })
                              }}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={12}>
                          <FormGroup className="mb-0">
                            <Label className="form-label" style={{ fontWeight: '500', fontSize: '14px', marginBottom: '10px' }}>
                              Trial Period {selectedAgentReferral && <span className="text-muted" style={{ fontWeight: '400', fontSize: '12px' }}>(Disabled — using referral code trial)</span>}
                            </Label>
                            <div style={{ opacity: selectedAgentReferral ? 0.4 : 1, pointerEvents: selectedAgentReferral ? 'none' : 'auto' }}>
                              <Controller
                                name="trialPeriod"
                                control={salesAgentControl}
                                render={({ field }) => (
                                  <div className="d-flex gap-4">
                                    <div className="form-check">
                                      <Input
                                        {...field}
                                        type="radio"
                                        id="agent-trial-14"
                                        value="14"
                                        checked={field.value === '14'}
                                        className="form-check-input"
                                        style={{ cursor: 'pointer' }}
                                      />
                                      <Label
                                        className="form-check-label"
                                        htmlFor="agent-trial-14"
                                        style={{ cursor: 'pointer', fontSize: '14px', fontWeight: '400' }}
                                      >
                                        14 Days
                                      </Label>
                                    </div>
                                    <div className="form-check">
                                      <Input
                                        {...field}
                                        type="radio"
                                        id="agent-trial-30"
                                        value="30"
                                        checked={field.value === '30'}
                                        className="form-check-input"
                                        style={{ cursor: 'pointer' }}
                                      />
                                      <Label
                                        className="form-check-label"
                                        htmlFor="agent-trial-30"
                                        style={{ cursor: 'pointer', fontSize: '14px', fontWeight: '400' }}
                                      >
                                        30 Days
                                      </Label>
                                    </div>
                                  </div>
                                )}
                              />
                              {salesAgentErrors.trialPeriod && (
                                <div className="text-danger" style={{ fontSize: '14px', marginTop: '4px' }}>
                                  {salesAgentErrors.trialPeriod.message}
                                </div>
                              )}
                            </div>
                          </FormGroup>
                        </Col>
                      </Row>
                      <div className="mt-4 pt-2 border-top">
                        <Button
                          color="primary"
                          type="submit"
                          disabled={salesAgentSubmitting}
                          size="lg"
                          style={{ minWidth: '200px', fontWeight: '500' }}
                        >
                          {salesAgentSubmitting && <Spinner size="sm" className="me-2" />}
                          {salesAgentSubmitting ? 'Sending Invite...' : 'Invite Sales Agent'}
                        </Button>
                      </div>
                    </form>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <InviteSuccessModal
        open={successModalOpen}
        organizationName={invitedOrganizationName}
        onClose={handleCloseSuccessModal}
        onViewManagement={() => {
          handleCloseSuccessModal();
          navigate('/management');
        }}
      />
    </div>
  );
}

const mapDispatchToProps = {
  inviteOrganization: inviteOrganizationAction
};

export default connect(null, mapDispatchToProps)(InvitePage);
